import type {
  ImageEntry,
  Platform,
  ReleaseVersion,
  ResourceCatalog,
  ResourceChannel,
  ResourceIdentity,
  ResourceIndex,
  ResourceStats,
  ResourceSummary,
  SortOption,
} from "./types";

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string");
}

function isValidDate(value: unknown): value is string {
  return isNonEmptyString(value) && !Number.isNaN(Date.parse(value));
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0;
}

function isValidPlatform(value: unknown): value is Platform {
  return (
    isObject(value) &&
    isNonEmptyString(value.architecture) &&
    isNonEmptyString(value.digest) &&
    isNonEmptyString(value.os) &&
    (value.size === null ||
      (typeof value.size === "number" && Number.isFinite(value.size) && value.size >= 0)) &&
    isNullableString(value.variant)
  );
}

function isValidRelease(value: unknown): value is ReleaseVersion {
  return (
    isObject(value) &&
    isNonNegativeInteger(value.major) &&
    isNonNegativeInteger(value.minor) &&
    isNonNegativeInteger(value.patch) &&
    (value.revision === null || isNonNegativeInteger(value.revision)) &&
    isNullableString(value.prerelease)
  );
}

function isValidImage(value: unknown): value is ImageEntry {
  return (
    isObject(value) &&
    isNonEmptyString(value.canonicalTag) &&
    isNullableString(value.channel) &&
    ["available", "unavailable"].includes(String(value.detailsStatus)) &&
    isNonEmptyString(value.digest) &&
    isNonEmptyString(value.digestCommand) &&
    Array.isArray(value.platforms) &&
    value.platforms.every(isValidPlatform) &&
    isNonEmptyString(value.pullCommand) &&
    isValidDate(value.pushedAt) &&
    (value.release === null || isValidRelease(value.release)) &&
    isStringArray(value.tags) &&
    value.tags.length > 0 &&
    value.tags.includes(value.canonicalTag)
  );
}

function isValidChannel(value: unknown): value is ResourceChannel {
  return isObject(value) && isNonEmptyString(value.id) && isNonEmptyString(value.label);
}

function isValidIdentity(value: unknown): value is ResourceIdentity {
  return (
    isObject(value) &&
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.slug) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.description) &&
    isNonEmptyString(value.repository) &&
    isNonEmptyString(value.dataPath) &&
    value.dataPath.startsWith("/data/resources/") &&
    Array.isArray(value.channels) &&
    value.channels.every(isValidChannel)
  );
}

function isValidStats(value: unknown): value is ResourceStats {
  return (
    isObject(value) &&
    isNonNegativeInteger(value.totalImages) &&
    value.totalImages > 0 &&
    isNonNegativeInteger(value.totalTags) &&
    value.totalTags >= value.totalImages &&
    isNonNegativeInteger(value.warnings)
  );
}

function isValidSummary(value: unknown): value is ResourceSummary {
  if (!isObject(value)) return false;
  return (
    isNonEmptyString(value.id) &&
    isNonEmptyString(value.slug) &&
    isNonEmptyString(value.name) &&
    isNonEmptyString(value.description) &&
    isNonEmptyString(value.repository) &&
    isNonEmptyString(value.dataPath) &&
    value.dataPath.startsWith("/data/resources/") &&
    isValidDate(value.latestPushedAt) &&
    isValidStats(value.stats)
  );
}

export function validateResourceIndex(value: unknown): ResourceIndex {
  if (
    !isObject(value) ||
    value.schemaVersion !== 2 ||
    !isValidDate(value.generatedAt) ||
    !Array.isArray(value.resources) ||
    value.resources.length === 0 ||
    !value.resources.every(isValidSummary)
  ) {
    throw new TypeError("资源索引数据不完整。");
  }
  const summaries = value.resources as ResourceSummary[];
  if (
    new Set(summaries.map((resource) => resource.id)).size !== summaries.length ||
    new Set(summaries.map((resource) => resource.slug)).size !== summaries.length ||
    new Set(summaries.map((resource) => resource.dataPath)).size !== summaries.length
  ) {
    throw new TypeError("资源索引身份字段必须唯一。");
  }
  return value as ResourceIndex;
}

export function validateResourceCatalog(value: unknown): ResourceCatalog {
  if (
    !isObject(value) ||
    value.schemaVersion !== 2 ||
    !isValidDate(value.generatedAt) ||
    !isValidIdentity(value.resource) ||
    !isValidStats(value.stats) ||
    !Array.isArray(value.images) ||
    value.images.length === 0 ||
    !value.images.every(isValidImage) ||
    !Array.isArray(value.warnings)
  ) {
    throw new TypeError("镜像资源数据不完整。");
  }

  const catalog = value as ResourceCatalog;
  if (
    catalog.stats.totalImages !== catalog.images.length ||
    catalog.stats.warnings !== catalog.warnings.length ||
    new Set(catalog.images.map((image) => image.canonicalTag)).size !== catalog.images.length
  ) {
    throw new TypeError("镜像资源统计或 tag 不一致。");
  }
  const channelIds = new Set(catalog.resource.channels.map((channel) => channel.id));
  if (catalog.images.some((image) => image.channel !== null && !channelIds.has(image.channel))) {
    throw new TypeError("镜像资源包含未声明的 channel。");
  }
  return catalog;
}

export function compareReleaseVersions(left: ReleaseVersion, right: ReleaseVersion): number {
  for (const key of ["major", "minor", "patch"] as const) {
    const difference = right[key] - left[key];
    if (difference !== 0) return difference;
  }
  if (left.prerelease === null && right.prerelease !== null) return -1;
  if (left.prerelease !== null && right.prerelease === null) return 1;
  const prereleaseDifference = (right.prerelease ?? "").localeCompare(left.prerelease ?? "", "en", {
    numeric: true,
  });
  if (prereleaseDifference !== 0) return prereleaseDifference;
  return (right.revision ?? -1) - (left.revision ?? -1);
}

export function compareVersions(left: ImageEntry, right: ImageEntry): number {
  if (left.release && right.release) return compareReleaseVersions(left.release, right.release);
  if (left.release) return -1;
  if (right.release) return 1;
  return left.canonicalTag.localeCompare(right.canonicalTag, "en", { numeric: true });
}

function compareVersionAscending(left: ImageEntry, right: ImageEntry): number {
  if (left.release && right.release) return -compareReleaseVersions(left.release, right.release);
  if (left.release) return -1;
  if (right.release) return 1;
  return left.canonicalTag.localeCompare(right.canonicalTag, "en", { numeric: true });
}

export function filterAndSortImages(
  images: ImageEntry[],
  query: string,
  versionFilter: string,
  sort: SortOption,
): ImageEntry[] {
  const queryTokens = query.trim().toLocaleLowerCase("zh-CN").split(/\s+/).filter(Boolean);
  const filtered = images.filter((image) => {
    const tagText = [image.canonicalTag, ...image.tags].join(" ").toLocaleLowerCase("zh-CN");
    const digestText = [
      image.digest,
      image.digestCommand,
      ...image.platforms.map((platform) => platform.digest),
    ]
      .join(" ")
      .toLocaleLowerCase("zh-CN");
    const generalText = [tagText, image.pullCommand, ...image.platforms.map(platformLabel)]
      .join(" ")
      .toLocaleLowerCase("zh-CN");
    const matchesQuery = queryTokens.every((token) => {
      if (/^\d+(?:\.\d*)*$/.test(token)) return tagText.includes(token);
      if (/^sha256:[a-f0-9]+$/.test(token) || /^[a-f0-9]{8,}$/.test(token)) {
        return digestText.includes(token);
      }
      return generalText.includes(token);
    });
    const matchesVersion =
      versionFilter.length === 0 ||
      image.channel === versionFilter ||
      (image.release !== null && String(image.release.major) === versionFilter);
    return matchesQuery && matchesVersion;
  });

  return filtered.sort((left, right) => {
    if (sort === "version-desc") return compareVersions(left, right);
    if (sort === "version-asc") return compareVersionAscending(left, right);
    const dateDifference = Date.parse(left.pushedAt) - Date.parse(right.pushedAt);
    const directedDifference = sort === "date-desc" ? -dateDifference : dateDifference;
    return directedDifference || left.canonicalTag.localeCompare(right.canonicalTag, "en");
  });
}

function platformLabel(platform: Platform): string {
  return `${platform.os}/${platform.architecture}${platform.variant ? `/${platform.variant}` : ""}`;
}

export function getMajorVersions(images: ImageEntry[]): string[] {
  return [...new Set(images.flatMap((image) => (image.release ? [image.release.major] : [])))]
    .sort((left, right) => right - left)
    .map(String);
}

export function getLatestStable(images: ImageEntry[]): ImageEntry | undefined {
  return images
    .filter((image) => image.release !== null && image.release.prerelease === null)
    .sort(compareVersions)[0];
}

export function formatBytes(value: number | null): string {
  if (value === null) return "大小未知";
  return new Intl.NumberFormat("zh-CN", {
    maximumFractionDigits: 2,
    style: "unit",
    unit: "gigabyte",
    unitDisplay: "short",
  }).format(value / 1_000_000_000);
}

export function formatDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "未知日期";
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeZone: "Asia/Shanghai",
  }).format(date);
}
